import { SalesReportSummary } from "../../../types/sale-reports.type";
import { formatNumber, numberWithCommas } from "../../../utils";
import { formatDateTimeBkk } from "../../../utils/date";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";

interface TripOverviewCardProps {
  reportSummary: SalesReportSummary;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

/**
 * เวลาออก / เวลาเข้า are trip-level readings that can cross midnight, so they are
 * shown as วัน-เวลา: a van back at 00:30 reads as an ambiguous bare time otherwise,
 * and branches could not tell which day it belonged to.
 *
 * Prefers the date-bearing `..._at` instant; falls back to the legacy time-only
 * string when the backend predates that field (or sends an unparseable value), so
 * an older API still renders something sensible rather than a blank cell.
 */
const tripTime = (
  isoAt: string | null | undefined,
  timeOnly: string | null | undefined,
): string => formatDateTimeBkk(isoAt) || timeOnly || "-";

/**
 * Labels stay on one line — they are short and fixed. Values must NOT: since
 * เวลาออก / เวลาเข้า became วัน-เวลา ("05/06/2026 06:15") the longest value in the
 * card no longer fits its column on narrower screens, and a nowrap value has
 * nothing to clip it, so it ran straight past the card border. Values wrap at the
 * space (date on one line, time on the next) and `overflowWrap` breaks a single
 * unbroken token as a last resort, so no value can escape its column at any width.
 */
const InfoRow = ({ label, value }: InfoRowProps) => (
  <Box mb={0.75} sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      fontWeight={600}
      sx={{ display: "block", whiteSpace: "nowrap", lineHeight: 1.3 }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: (theme) => theme.palette.gray.main,
        overflowWrap: "anywhere",
        lineHeight: 1.3,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const Divider = () => (
  <Box
    sx={{
      width: "1px",
      mx: 1,
      flexShrink: 0,
      background: `repeating-linear-gradient(
        to bottom,
        transparent 0,
        transparent 3px,
        #AEAEAE 3px,
        #AEAEAE 6px
      )`,
    }}
  />
);

export default function TripOverviewCard({
  reportSummary,
}: TripOverviewCardProps) {
  return (
    <Card
      sx={{
        border: (theme) => `1px solid ${theme.palette.primary.main}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "none",
        borderRadius: "12px",
      }}
    >
      <CardContent
        sx={{ padding: 0, width: "100%", "&:last-child": { padding: 0 } }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <DirectionsCarFilledOutlinedIcon
            color="primary"
            sx={{ fontSize: "16px" }}
          />
          <Typography variant="caption" color="primary" fontWeight={700}>
            ข้อมูลการเดินทาง
          </Typography>
        </Box>
        <Box display="flex" mt={0.5}>
          <Box sx={{ flex: "3.5 1 0", minWidth: 0 }}>
            <InfoRow
              label="เติมน้ำมัน"
              value={`${formatNumber(reportSummary.fuel_bath)} บาท`}
            />
            <InfoRow
              label="จำนวนลิตร"
              value={`${numberWithCommas(reportSummary.fuel_liter)} ลิตร`}
            />
            <InfoRow
              label="ต้นทุนสาขา"
              value={`${formatNumber(reportSummary.branch_cost)} บาท`}
            />
          </Box>
          <Divider />
          {/* Mileage carries the shortest values ("12,345 km"), so it gives up
              the width the วัน-เวลา column needs to keep its value on one line. */}
          <Box sx={{ flex: "2.5 1 0", minWidth: 0 }}>
            <InfoRow
              label="ไมล์ออก"
              value={`${numberWithCommas(reportSummary.miles_out)} km`}
            />
            <InfoRow
              label="ไมล์เข้า"
              value={`${numberWithCommas(reportSummary.miles_in)} km`}
            />
            <InfoRow
              label="ระยะทาง"
              value={`${numberWithCommas(reportSummary.distance)} km`}
            />
          </Box>
          <Divider />
          <Box sx={{ flex: "3 1 0", minWidth: 0 }}>
            <InfoRow
              label="เวลาออก"
              value={tripTime(
                reportSummary.time_leave_branch_at,
                reportSummary.time_leave_branch,
              )}
            />
            <InfoRow
              label="เวลาเข้า"
              value={tripTime(
                reportSummary.time_return_branch_at,
                reportSummary.time_return_branch,
              )}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
